import {
  type ClientMsg,
  ClientMsgKind,
  ClientMsgSchema,
  type ServerMsg,
  ServerMsgKind,
  ServerMsgSchema,
} from '@infiltre/shared';
import { Value } from '@sinclair/typebox/value';
import { resolveBaseUrl } from '@/shared/lib/api';
import { refreshAuthToken } from '@/shared/lib/auth/refresh';
import { Emitter } from './emitter';
import type { FrameFilter } from './replay-filter';
import { DriverMode, type DriverStatus, DriverStatusKind, type GameDriver } from './types';

const BACKOFF_MS = [250, 500, 1000, 2000, 5000] as const;
const MAX_BACKOFF = 5000;

const jitter = (ms: number): number => ms + Math.floor(Math.random() * (ms / 4));

/** Wider than server 25s+10s budget — single missed tick must not churn. */
const SERVER_HEARTBEAT_WATCHDOG_MS = 40_000;

const PENDING_CAP = 256;
const PENDING_STORAGE_KEY = 'infiltre:pending:v1';

const CLOSE_CODE_UNAUTHORIZED = [1008, 4401] as const;
const CLOSE_CODE_ROOM_NOT_FOUND = 4404;
const CLOSE_CODE_ROOM_FULL = 4409;
const CLOSE_CODE_KICKED = 4410;
const CLOSE_CODE_REPLACED = 4002;

const SEMANTIC_CLOSE_STATUS: Record<number, DriverStatus> = {
  [CLOSE_CODE_ROOM_NOT_FOUND]: { kind: DriverStatusKind.RoomNotFound },
  [CLOSE_CODE_ROOM_FULL]: { kind: DriverStatusKind.RoomFull },
  [CLOSE_CODE_KICKED]: { kind: DriverStatusKind.KickedDisconnected },
  // Replaced by another device — user explicitly chose the other one, don't reconnect.
  [CLOSE_CODE_REPLACED]: { kind: DriverStatusKind.Unauthorized },
};

interface PendingFrame {
  nonce: string;
  frame: string;
  /** Used by dedup so two `OpenVote`s collapse. */
  kind: string;
}

const newNonce = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `n-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const dedupQueue = (queue: readonly PendingFrame[]): PendingFrame[] => {
  // Collapse same-kind runs — host double-tap must not multi-fire on reopen.
  const seen = new Set<string>();
  const out: PendingFrame[] = [];
  // Newest → oldest to keep latest intent.
  for (let i = queue.length - 1; i >= 0; i--) {
    const f = queue[i]!;
    if (seen.has(f.kind)) continue;
    seen.add(f.kind);
    out.push(f);
  }
  return out.reverse();
};

const persistQueue = (queue: readonly PendingFrame[]): void => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // quota / disabled — degrade silently
  }
};

const restoreQueue = (): PendingFrame[] => {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PendingFrame[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is PendingFrame =>
        typeof p?.nonce === 'string' && typeof p?.frame === 'string' && typeof p?.kind === 'string',
    );
  } catch {
    return [];
  }
};

const clearPersistedQueue = (): void => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(PENDING_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Online `GameDriver` over a single WebSocket. Server rehydrates via `RoomState`
 * on each open — no app-level resume protocol. Token in `?token=` (browsers can't
 * set headers on WS upgrades). Inbound TypeBox-validated. Heartbeat auto-pongs +
 * watchdog forces close on silence (half-open TCP). Backoff w/ jitter caps 5s.
 * 1008/4401 → single refresh + retry, else `Unauthorized`. Pending queue persisted
 * to sessionStorage and replayed (kind-deduped) on reopen.
 */
export class RemoteGameDriver implements GameDriver {
  readonly mode = DriverMode.Online;

  private socket: WebSocket | null = null;
  private currentStatus: DriverStatus = { kind: DriverStatusKind.Idle };
  private readonly messages = new Emitter<ServerMsg>();
  private readonly statusEvents = new Emitter<DriverStatus>();
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatWatchdog: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private refreshUsed = false;
  private pending: PendingFrame[] = restoreQueue();

  constructor(
    private readonly roomId: string,
    private readonly getToken: () => string | null,
    private readonly baseUrl: string = resolveBaseUrl(),
    /** Sink for refreshed token so identity store / HTTP client stay in sync. */
    private readonly setToken: (token: string) => void = () => {
      // no-op default — caller wires real sink when needed
    },
    /**
     * Optional pre-flush filter. Frames returning `false` are silently dropped
     * from the pending queue on reconnect — used to skip stale actions whose
     * preconditions (phase, target alive) no longer hold.
     */
    private readonly frameFilter: FrameFilter = () => true,
  ) {}

  status(): DriverStatus {
    return this.currentStatus;
  }

  async start(): Promise<void> {
    this.stopped = false;
    this.refreshUsed = false;
    this.connect();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.clearWatchdog();
    this.socket?.close();
    this.socket = null;
    this.pending = [];
    clearPersistedQueue();
    this.setStatus({ kind: DriverStatusKind.Idle });
    this.messages.clear();
    this.statusEvents.clear();
  }

  send(msg: ClientMsg): void {
    if (this.stopped) return;
    const validated = Value.Parse(ClientMsgSchema, msg);
    const frame = JSON.stringify(validated);
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(frame);
      return;
    }
    // Park frame; persisted so tab reload mid-disconnect doesn't lose action.
    const entry: PendingFrame = {
      nonce: newNonce(),
      frame,
      kind: validated.kind,
    };
    this.pending.push(entry);
    if (this.pending.length > PENDING_CAP) {
      this.pending.splice(0, this.pending.length - PENDING_CAP);
    }
    persistQueue(this.pending);
  }

  onMessage(handler: (m: ServerMsg) => void) {
    return this.messages.on(handler);
  }

  onStatus(handler: (s: DriverStatus) => void) {
    return this.statusEvents.on(handler);
  }

  private connect(): void {
    const token = this.getToken();
    if (!token) {
      this.setStatus({ kind: DriverStatusKind.Unauthorized });
      return;
    }
    const url = this.wsUrl(token);
    if (this.retryAttempt === 0) {
      this.setStatus({ kind: DriverStatusKind.Connecting });
    } else {
      this.setStatus({ kind: DriverStatusKind.Reconnecting, attempt: this.retryAttempt });
    }
    const ws = new WebSocket(url);
    this.socket = ws;

    ws.addEventListener('open', () => {
      this.retryAttempt = 0;
      this.refreshUsed = false;
      this.setStatus({ kind: DriverStatusKind.Ready });
      this.armWatchdog();
      // Splice (not forEach) so re-close mid-flush leaves unsent tail in `pending`.
      const flush = dedupQueue(this.pending).filter((entry) => this.shouldReplay(entry));
      this.pending = [];
      clearPersistedQueue();
      while (flush.length > 0 && ws.readyState === WebSocket.OPEN) {
        const next = flush.shift();
        if (next) ws.send(next.frame);
      }
      if (flush.length > 0) {
        this.pending = flush;
        persistQueue(this.pending);
      }
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      this.handleFrame(event.data);
    });

    ws.addEventListener('error', () => {
      // Close handler owns retry decision.
    });

    ws.addEventListener('close', (event: CloseEvent) => this.handleSocketClose(event));
  }

  /**
   * Decide whether a pending frame is still worth replaying. Parse failures
   * fall through to the server — the schema-check on `send()` already vetted
   * them, so a parse miss here means we lost the type info, not invalidity.
   */
  private shouldReplay(entry: PendingFrame): boolean {
    let msg: ClientMsg;
    try {
      msg = Value.Parse(ClientMsgSchema, JSON.parse(entry.frame));
    } catch {
      return true;
    }
    return this.frameFilter(msg);
  }

  private handleSocketClose(event: CloseEvent): void {
    this.socket = null;
    this.clearWatchdog();
    if (this.stopped) return;

    const semantic = SEMANTIC_CLOSE_STATUS[event.code];
    if (semantic !== undefined) {
      this.setStatus(semantic);
      return;
    }

    // 1008/4401 → single refresh attempt then retry or Unauthorized.
    if ((CLOSE_CODE_UNAUTHORIZED as readonly number[]).includes(event.code)) {
      this.handleUnauthorizedClose();
      return;
    }

    this.scheduleReconnect();
  }

  private handleUnauthorizedClose(): void {
    if (this.refreshUsed) {
      this.setStatus({ kind: DriverStatusKind.Unauthorized });
      return;
    }
    this.refreshUsed = true;
    const current = this.getToken();
    if (!current) {
      this.setStatus({ kind: DriverStatusKind.Unauthorized });
      return;
    }
    void refreshAuthToken(current, this.baseUrl).then((next) => {
      if (this.stopped) return;
      if (!next) {
        this.setStatus({ kind: DriverStatusKind.Unauthorized });
        return;
      }
      this.setToken(next);
      this.connect();
    });
  }

  private handleFrame(raw: unknown): void {
    let payload: unknown;
    if (typeof raw === 'string') {
      try {
        payload = JSON.parse(raw);
      } catch {
        return;
      }
    } else {
      payload = raw;
    }
    let msg: ServerMsg;
    try {
      msg = Value.Parse(ServerMsgSchema, payload);
    } catch {
      return;
    }
    // Any inbound frame keeps watchdog healthy; Heartbeat handled internally.
    this.armWatchdog();
    if (msg.kind === ServerMsgKind.Heartbeat) {
      this.replyPong(msg.seq);
      return;
    }
    this.messages.emit(msg);
  }

  private replyPong(seq: number): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const pong: ClientMsg = {
      kind: ClientMsgKind.Pong,
      // Server resolves identity from socket; field is schema-required only.
      actorPlayerId: 'self',
      seq,
    };
    try {
      this.socket.send(JSON.stringify(pong));
    } catch {
      // Socket closing — close handler retries.
    }
  }

  private armWatchdog(): void {
    this.clearWatchdog();
    this.heartbeatWatchdog = setTimeout(() => {
      this.heartbeatWatchdog = null;
      // Silent window → half-open; force-close to trigger reconnect.
      try {
        this.socket?.close();
      } catch {
        // already closed
      }
    }, SERVER_HEARTBEAT_WATCHDOG_MS);
  }

  private clearWatchdog(): void {
    if (!this.heartbeatWatchdog) return;
    clearTimeout(this.heartbeatWatchdog);
    this.heartbeatWatchdog = null;
  }

  private scheduleReconnect(): void {
    const delay = jitter(
      BACKOFF_MS[Math.min(this.retryAttempt, BACKOFF_MS.length - 1)] ?? MAX_BACKOFF,
    );
    this.retryAttempt += 1;
    this.setStatus({ kind: DriverStatusKind.Reconnecting, attempt: this.retryAttempt });
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.stopped) return;
      this.connect();
    }, delay);
  }

  private setStatus(s: DriverStatus): void {
    this.currentStatus = s;
    this.statusEvents.emit(s);
  }

  private wsUrl(token: string): string {
    const base = this.baseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    return `${base}/ws/${encodeURIComponent(this.roomId)}?token=${encodeURIComponent(token)}`;
  }
}
